import { useState } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Button,
  Textarea,
  SplitButton,
} from '@duck/ui'
import { apis } from '@/modules/apiClients'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useCohort } from '@/hooks/useCohorts'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'
import { SendAssignment } from '../Assignments/SendAssignment'

interface AddCohortPersonDialogProps {
  isOpen: boolean
  onClose: () => void
  cohortId: string
  onPersonAdded?: (createdPersonIds: string[]) => void
  onAssignmentSent?: () => void
}

type SubmitMode = 'just-add' | 'add-and-assign'

export const AddCohortPersonDialog = ({
  isOpen,
  onClose,
  cohortId,
  onPersonAdded,
  onAssignmentSent,
}: AddCohortPersonDialogProps) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { cohort, refetch: refetchCohort } = useCohort(cohortId)
  const { getOrgSetting } = useOrganizationSettings()
  const invitationEmails = getOrgSetting('invitationEmails') ?? false
  const [activeTab, setActiveTab] = useState<'single' | 'multiple'>('single')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMode, setSubmitMode] = useState<SubmitMode>('add-and-assign')
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'add' | 'send'>('add')
  const [createdPersonIds, setCreatedPersonIds] = useState<string[]>([])

  // Multiple people CSV state
  const [csvInput, setCsvInput] = useState('')
  const [parsedRows, setParsedRows] = useState<
    { firstName: string; lastName: string; email: string; isEmailValid: boolean }[]
  >([])

  // Single person form state
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const resetForm = () => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setError(null)
    setCsvInput('')
    setParsedRows([])
    setActiveTab('single')
    setStep('add')
    setCreatedPersonIds([])
    setSubmitMode('add-and-assign')
  }

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm()
      onClose()
    }
  }

  const handleSubmit = async (mode: SubmitMode) => {
    setError(null)

    if (!cohort) {
      setError('Not found. Please refresh the page and try again.')
      return
    }

    try {
      setIsSubmitting(true)

      let createdPersonIds: string[] = []

      if (activeTab === 'single') {
        if (!email || !firstName || !lastName) {
          setError('Email, first name, and last name are required.')
          return
        }

        const createPersonRequest: UserService.CreatePersonRequest = {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          organizationId: cohort.organizationId,
        }

        const response = await apis.partnerService.cohortsAddPeoplePost({
          cohortId,
          people: [createPersonRequest],
        })
        createdPersonIds = response.data.createdPersonIds ?? []
      } else {
        if (parsedRows.length === 0) {
          setError('Please add at least one person.')
          return
        }

        if (parsedRows.some((row) => !row.isEmailValid)) {
          setError('Please fix invalid email addresses before submitting.')
          return
        }

        const people: UserService.CreatePersonRequest[] = parsedRows.map((row) => ({
          email: row.email.trim(),
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          organizationId: cohort.organizationId,
        }))

        const response = await apis.partnerService.cohortsAddPeoplePost({
          cohortId,
          people,
        })
        createdPersonIds = response.data.createdPersonIds ?? []
      }

      await refetchCohort()

      onPersonAdded?.(createdPersonIds)

      if (mode === 'add-and-assign' && createdPersonIds.length > 0) {
        setCreatedPersonIds(createdPersonIds)
        setStep('send')
      } else {
        resetForm()
        onClose()
      }
    } catch (err: unknown) {
      console.error('Failed to add person to cohort:', err)
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to add person to cohort. Please try again.'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCsvChange = (value: string) => {
    setCsvInput(value)

    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    const rows: {
      firstName: string
      lastName: string
      email: string
      isEmailValid: boolean
    }[] = []

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    for (const line of lines) {
      const [firstName = '', lastName = '', email = ''] = line.split(',').map((part) => part.trim())

      if (!firstName && !lastName && !email) {
        continue
      }

      const isEmailValid = email.length === 0 ? false : emailRegex.test(email)

      rows.push({ firstName, lastName, email, isEmailValid })
    }

    setParsedRows(rows)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSubmitting && !open && handleClose()}
    >
      <DialogContent
        width="sm"
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onInteractOutside={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {step === 'send'
              ? (invitationEmails ? t('partner.sendAssignment') : t('partner.newAssignment'))
              : tP('addPerson')}
          </DialogTitle>
        </DialogHeader>

        {step === 'send' ? (
          <SendAssignment
            cohortId={cohortId}
            personIds={createdPersonIds}
            sendInvitationEmails={invitationEmails}
            publicMode={false}
            onClose={() => {
              resetForm()
              onClose()
            }}
            onSend={() => {
              resetForm()
              onClose()
              onAssignmentSent?.()
            }}
          />
        ) : (
        <>
        <div className="flex gap-2">
          <Button
            type="button"
            size="xs"
            variant={activeTab === 'single' ? 'default' : 'outline'}
            onClick={() => setActiveTab('single')}
            disabled={isSubmitting}
          >
            {t('common.single')}
          </Button>
          <Button
            type="button"
            size="xs"
            variant={activeTab === 'multiple' ? 'default' : 'outline'}
            onClick={() => setActiveTab('multiple')}
            disabled={isSubmitting}
          >
            {t('common.multiple')}
          </Button>
        </div>

        {activeTab === 'single' && (
          <div className="space-y-4 mt-4">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void handleSubmit(submitMode)
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <label htmlFor="person-email" className="block text-sm font-medium text-foreground">
                  {t('common.email')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-email"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.email').toLowerCase() })}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="person-firstName" className="block text-sm font-medium text-foreground">
                  {t('common.firstName')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-firstName"
                  type="text"
                  autoComplete="off"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.firstName').toLowerCase() })}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="person-lastName" className="block text-sm font-medium text-foreground">
                  {t('common.lastName')}
                  <span className="text-error ms-1">*</span>
                </label>
                <Input
                  id="person-lastName"
                  type="text"
                  autoComplete="off"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('common.enterValue', { value: t('common.lastName').toLowerCase() })}
                />
              </div>

              {error && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <div className="flex flex-row gap-3 pt-4 border-t border-border items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <SplitButton
                  value={submitMode}
                  onValueChange={(v) => setSubmitMode(v as SubmitMode)}
                  loading={isSubmitting}
                  loadingLabel={t('common.saving')}
                  options={[
                    {
                      value: 'add-and-assign',
                      label: t('partner.addAndSendAssignment'),
                      onSelect: () => void handleSubmit('add-and-assign'),
                    },
                    {
                      value: 'just-add',
                      label: t('common.justAdd'),
                      onSelect: () => void handleSubmit('just-add'),
                    },
                  ]}
                />
              </div>
            </form>
          </div>
        )}

        {activeTab === 'multiple' && (
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="csv-input"
                className="block text-sm font-medium text-foreground"
              >
                CSV ({'firstName,lastName,email'})
              </label>
              <Textarea
                id="csv-input"
                autoComplete="off"
                value={csvInput}
                onChange={(e) => handleCsvChange(e.target.value)}
                disabled={isSubmitting}
                placeholder="firstName,lastName,email&#10;Jane,Doe,jane@example.com&#10;John,Smith,john@company.com"
                className="min-h-[140px] resize-y font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Each line should contain{' '}
                <span className="font-semibold">firstName,lastName,email</span>{' '}
                separated by commas.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {parsedRows.length > 0 && (
              <div className="space-y-3">
                {(() => {
                  const failedCount = parsedRows.filter((row) => !row.isEmailValid).length

                  return (
                    <p className="text-xs text-muted-foreground">
                      Preview ({parsedRows.length} row{parsedRows.length === 1 ? '' : 's'}
                      {failedCount > 0 && (
                        <>
                          ,{' '}
                          <span className="text-warning font-medium">
                            {failedCount} invalid
                          </span>
                        </>
                      )}
                      ):
                    </p>
                  )
                })()}

                <div className="max-h-48 overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.firstName')}
                        </th>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.lastName')}
                        </th>
                        <th className="px-3 py-2 text-start font-medium text-muted-foreground">
                          {t('common.email')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, index) => (
                        <tr
                          key={`${row.email || index}-${index}`}
                          className={index % 2 === 0 ? 'bg-background' : 'bg-muted/40'}
                        >
                          <td className="px-3 py-1.5 align-top break-words">
                            {row.firstName}
                          </td>
                          <td className="px-3 py-1.5 align-top break-words">
                            {row.lastName}
                          </td>
                          <td
                            className={`px-3 py-1.5 align-top break-words ${row.isEmailValid ? '' : 'border border-error'
                              }`}
                          >
                            {row.email}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-2">
                  <SplitButton
                    value={submitMode}
                    onValueChange={(v) => setSubmitMode(v as SubmitMode)}
                    loading={isSubmitting}
                    loadingLabel={t('common.saving')}
                    size="sm"
                    disabled={
                      parsedRows.length === 0 ||
                      parsedRows.some((row) => !row.isEmailValid)
                    }
                    options={[
                      {
                        value: 'add-and-assign',
                        label: t('partner.addAndSendAssignment'),
                        onSelect: () => void handleSubmit('add-and-assign'),
                      },
                      {
                        value: 'just-add',
                        label: t('common.justAdd'),
                        onSelect: () => void handleSubmit('just-add'),
                      },
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
